import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Modal, Group, Button, Checkbox, Text, TextInput, Select, SegmentedControl, Badge, Skeleton, Stack, Box, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconCopy, IconInfoCircle, IconArrowRight } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CampaignApi from "../../api/CampaignApi";
import GeneralComponentApi from "../../api/GeneralComponentApi";
import { SmartCodeNotDetectedModal } from "../Funnel/SmartCodeNotDetectedModal";
import { smartCodeGuideFor } from "../GeneralComponent/smartCodeGuides";
import { useAuth } from '../../AuthContext';

// Existing-page option: pages that are already a campaign LP are listed but disabled
const LpPageOption = forwardRef<HTMLDivElement, any>(({ label, url, activeCampaign, ...others }, ref) => (
  <div ref={ref} {...others}>
    <Group noWrap position="apart" spacing={8}>
      <Box style={{ minWidth: 0 }}>
        <Text fz={14} truncate>{label}</Text>
        {url && (<Text fz={12} c="dimmed" truncate>{url}</Text>)}
      </Box>
      {activeCampaign && (<Badge color="orange" variant="light" radius="sm" style={{ flexShrink: 0 }}>Active Campaign</Badge>)}
    </Group>
  </div>
));

export function DuplicateCampaign({ duplicateCampaignModalOpen, setDuplicateCampaignModalOpen, selectedMemberWebsite, sourceCampaign, activeCampaignPageIds, onDuplicated }:any) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const dark = theme.colorScheme === 'dark';
  const primary = theme.colors[theme.primaryColor];
  const cardBg = dark ? theme.colors.dark[6] : theme.colors.gray[0];
  const cardBorder = dark ? theme.colors.dark[4] : theme.colors.gray[3];
  const { websiteListData } = useAuth();
  const selectedWebsite = useMemo(
    () => (websiteListData || []).find((w:any) => String(w.website_id) === String(selectedMemberWebsite)),
    [websiteListData, selectedMemberWebsite]
  );
  const smartCodeGuideUrl = useMemo(
    () => smartCodeGuideFor(selectedWebsite?.front_end_framework, selectedWebsite?.framework_type),
    [selectedWebsite]
  );
  const [landingPage, setLandingPage] = useState<any>(null);
  const [conversionPages, setConversionPages] = useState([]);
  const [checkedPages, setCheckedPages] = useState<string[]>([]);
  const [lpPageType, setLpPageType] = useState('existing');
  const [websitePages, setWebsitePages] = useState([]);
  const [existingLpPageId, setExistingLpPageId] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ url: '', lp_page: '', campaign_name: '' });
  const [pagesLoading, setPagesLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [smartCodeDialogOpen, setSmartCodeDialogOpen] = useState(false);

  const handleApiResponse = (response:any) => {
    if (response.ok) {
      return response.json();
    }
    if (response.status === 401 || response.status === 400) {
      navigate("/logout");
    }
  };

  // sourceCampaign is the clicked row of the campaign list (encrypted campaign_id, funnel_id, campaign_page)
  useEffect(() => {
    if (!duplicateCampaignModalOpen || !sourceCampaign) {
      return;
    }
    let isCurrentRequest = true;
    setLpPageType('existing');
    setExistingLpPageId('');
    setLandingPageUrl('');
    setNewCampaignName('');
    setFieldErrors({ url: '', lp_page: '', campaign_name: '' });
    setLandingPage(null);
    setWebsitePages([]);
    setConversionPages([]);
    setCheckedPages([]);
    setPagesLoading(true);
    Promise.all([
      CampaignApi.getcampaignpagebyfunnelid({
        funnel_id: sourceCampaign.funnel_id,
        system_website_id: selectedMemberWebsite
      }).then(handleApiResponse),
      GeneralComponentApi.getwebsitepages({
        system_website_id: selectedMemberWebsite,
        type: 'source'
      }).then(handleApiResponse)
    ])
    .then(([data, pagesData]) => {
      if (!isCurrentRequest) {
        return;
      }
      if (data && data.status === 1) {
        const funnelPages = data.data || [];
        // The landing page is replaced by the new LP, every other funnel page is a conversion page
        const pages = funnelPages.filter((page:any) => page.page_id !== sourceCampaign.campaign_page);
        setLandingPage(funnelPages.find((page:any) => page.page_id === sourceCampaign.campaign_page) || null);
        setConversionPages(pages);
        setCheckedPages(pages.map((page:any) => page.page_id));
      }
      if (pagesData && pagesData.status === 1) {
        // 'LTI=' is the "All pages" option of this shared endpoint
        setWebsitePages((pagesData.data || []).filter((page:any) => page.page_id !== 'LTI='));
      }
      setPagesLoading(false);
    })
    .catch(error => {
      console.error("API Error:", error);
      setPagesLoading(false);
    });
    return () => {
      isCurrentRequest = false;
    };
  }, [duplicateCampaignModalOpen, sourceCampaign]);

  const handleDuplicateCampaignCloseModal = () => {
    if (!duplicating) {
      setSmartCodeDialogOpen(false);
      setDuplicateCampaignModalOpen(false);
    }
  };

  const togglePage = (pageId:string) => {
    setCheckedPages(prev => prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]);
  };

  const existingLpOptions = useMemo(() => websitePages.map((page:any) => {
    const activeCampaign = (activeCampaignPageIds || []).includes(page.page_id);
    return { value: page.page_id, label: page.custom_title || page.page_url, url: page.page_url, activeCampaign, disabled: activeCampaign };
  }), [websitePages, activeCampaignPageIds]);
  const selectedExistingLp:any = existingLpOptions.find((option:any) => option.value === existingLpPageId);
  // An existing LP chosen from the funnel is no longer offered as a conversion page
  const visibleConversionPages = lpPageType === 'existing' ? conversionPages.filter((page:any) => page.page_id !== existingLpPageId) : conversionPages;
  const submittedConversionPages = checkedPages.filter((pageId) => visibleConversionPages.some((page:any) => page.page_id === pageId));

  // Resolves true once the campaign is created; force skips the SmartCode check ("Continue Anyway")
  const submitDuplicateCampaign = async (force:boolean) => {
    const errors = {
      url: lpPageType === 'new' && landingPageUrl.trim() === '' ? 'Landing Page URL is Required field' : '',
      lp_page: lpPageType === 'existing' && existingLpPageId === '' ? 'Please select a landing page' : '',
      campaign_name: newCampaignName.trim() === '' ? 'Campaign Name is Required field' : ''
    };
    setFieldErrors(errors);
    if (!sourceCampaign || errors.url !== '' || errors.lp_page !== '' || errors.campaign_name !== '') {
      return false;
    }
    setDuplicating(true);
    try {
      let no_of_pages_access, no_of_funnels_access;
      const userData = JSON.parse(atob(localStorage.getItem('visiUserData')));
      if (userData && userData.no_of_add_access) {
        no_of_pages_access = userData.no_of_add_access.no_of_pages_access;
        no_of_funnels_access = userData.no_of_add_access.no_of_funnels_access;
      }
      const APIResponse = await CampaignApi.duplicatecampaign({
        system_website_id: selectedMemberWebsite,
        c_id: sourceCampaign.campaign_id,
        campaign_name: newCampaignName.trim(),
        lp_page_type: lpPageType,
        ...(lpPageType === 'existing' ? { campaign_page_id: existingLpPageId } : { url: landingPageUrl.trim() }),
        conversion_page_ids: submittedConversionPages,
        no_of_pages_access: no_of_pages_access,
        account_funnel_limit: no_of_funnels_access,
        ...(force ? { force_add: 1 } : {})
      });
      const APIResultJSON = await handleApiResponse(APIResponse);
      setDuplicating(false);
      if (!APIResultJSON) {
        return false;
      }
      if (APIResultJSON.status === 1) {
        notifications.show({
          color: 'green',
          icon: <IconCheck size="1.1rem" />,
          title: 'Success',
          message: APIResultJSON.message,
        });
        setSmartCodeDialogOpen(false);
        setDuplicateCampaignModalOpen(false);
        onDuplicated(APIResultJSON.data);
        return true;
      }
      if (APIResultJSON.error_code === 'smartcode_not_detected') {
        setSmartCodeDialogOpen(true);
        return false;
      }
      setSmartCodeDialogOpen(false);
      if (['page_exists', 'domain_mismatch'].includes(APIResultJSON.error_code)) {
        setFieldErrors({ url: APIResultJSON.message, lp_page: '', campaign_name: '' });
      } else if (['lp_page_in_use', 'lp_page_invalid'].includes(APIResultJSON.error_code)) {
        setFieldErrors({ url: '', lp_page: APIResultJSON.message, campaign_name: '' });
      } else if (['name_exists', 'name_invalid'].includes(APIResultJSON.error_code)) {
        setFieldErrors({ url: '', lp_page: '', campaign_name: APIResultJSON.message });
      } else {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: APIResultJSON.message,
        });
      }
      return false;
    } catch (error) {
      console.log('error', error);
      setDuplicating(false);
      return false;
    }
  };

  return (
    <>
      <SmartCodeNotDetectedModal
        opened={smartCodeDialogOpen}
        pageUrl={landingPageUrl.trim()}
        guideUrl={smartCodeGuideUrl}
        onClose={() => setSmartCodeDialogOpen(false)}
        onRecheck={() => submitDuplicateCampaign(false)}
        onContinueAnyway={() => submitDuplicateCampaign(true)}
      />
      <Modal
        opened={duplicateCampaignModalOpen}
        onClose={handleDuplicateCampaignCloseModal}
        radius="lg"
        size={600}
        padding="xl"
        trapFocus={false}
        title={
          <Group spacing={10} noWrap>
            <Box style={{ width: 32, height: 32, borderRadius: 8, display: 'grid', placeItems: 'center', background: dark ? theme.fn.rgba(primary[8], 0.25) : primary[0], color: dark ? primary[3] : primary[7] }}>
              <IconCopy size={18} />
            </Box>
            <Text fw={600} fz={16}>Duplicate Campaign</Text>
          </Group>
        }
      >
        <Text fz={14} c="dimmed" lh={1.6} mb={18}>
          Copy <b>{sourceCampaign?.campaign_name}</b> with the same funnel, goals, tracking and webhooks on another landing page. Pick an existing page or add a new one, enter a name, and choose the conversion pages to bring across.
        </Text>
        <Stack spacing={16}>
          <Box>
            <Text fz={14} fw={500} mb={6}>LP Page</Text>
            <SegmentedControl
              fullWidth
              radius="md"
              value={lpPageType}
              onChange={(value) => {
                setLpPageType(value);
                setFieldErrors({ url: '', lp_page: '', campaign_name: fieldErrors.campaign_name });
              }}
              data={[{ label: 'Existing Page', value: 'existing' }, { label: 'New Page', value: 'new' }]}
              disabled={duplicating}
            />
          </Box>
          {lpPageType === 'existing' ? (
            <Select
              label="Select existing LP page"
              placeholder={pagesLoading ? 'Loading pages…' : (existingLpOptions.length > 0 ? 'Select page' : 'No pages available')}
              data={existingLpOptions}
              itemComponent={LpPageOption}
              value={existingLpPageId}
              onChange={(value) => setExistingLpPageId(value || '')}
              error={fieldErrors.lp_page}
              nothingFound="No pages found"
              searchable
              withinPortal
              maxDropdownHeight={280}
              radius="md"
              size="md"
              disabled={duplicating || pagesLoading}
            />
          ) : (
            <TextInput
              label="New landing page URL"
              placeholder="https://www.example.com/new-landing-page"
              value={landingPageUrl}
              onChange={(event) => setLandingPageUrl(event.currentTarget.value)}
              error={fieldErrors.url}
              radius="md"
              size="md"
              disabled={duplicating}
            />
          )}
          <TextInput
            label="New campaign name"
            placeholder="Campaign name (also used for the new funnel)"
            value={newCampaignName}
            onChange={(event) => setNewCampaignName(event.currentTarget.value)}
            error={fieldErrors.campaign_name}
            radius="md"
            size="md"
            disabled={duplicating}
          />
          {lpPageType === 'new' && (
            <Group spacing={10} noWrap align="flex-start" style={{ background: dark ? theme.fn.rgba(primary[8], 0.18) : primary[0], border: `1px solid ${dark ? theme.fn.rgba(primary[6], 0.35) : primary[2]}`, borderRadius: 10, padding: '10px 12px' }}>
              <IconInfoCircle size={18} style={{ flexShrink: 0, marginTop: 1, color: dark ? primary[3] : primary[7] }} />
              <Text fz={13} lh={1.5} c={dark ? primary[2] : primary[9]}>Add the Visiopt SmartCode to the new landing page before creating the campaign.</Text>
            </Group>
          )}
          {pagesLoading ? (
              <Stack spacing="sm">
                <Skeleton height={16} width="40%" radius="sm" />
                <Skeleton height={64} radius="md" />
              </Stack>
            ) : (
              <>
                {landingPage && (
                  <Box>
                    <Text fz={12} fw={700} c="dimmed" mb={8} style={{ letterSpacing: '0.06em' }}>LANDING PAGE</Text>
                    <Group spacing={10} noWrap style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '12px 16px' }}>
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text fz={14} fw={600} td="line-through" c="dimmed" truncate>{landingPage.title}</Text>
                        <Text fz={12.5} c="dimmed" style={{ fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}>{landingPage.url}</Text>
                      </Box>
                      <IconArrowRight size={18} style={{ flexShrink: 0, color: dark ? primary[3] : primary[7] }} />
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text fz={14} fw={600} truncate>{lpPageType === 'existing' ? (selectedExistingLp ? selectedExistingLp.label : 'Existing page') : 'New landing page'}</Text>
                        <Text fz={12.5} c="dimmed" style={{ fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}>{(lpPageType === 'existing' ? (selectedExistingLp ? selectedExistingLp.url : '') : landingPageUrl.trim()) || '—'}</Text>
                      </Box>
                    </Group>
                  </Box>
                )}
                <Box>
                  <Text fz={12} fw={700} c="dimmed" mb={8} style={{ letterSpacing: '0.06em' }}>CONVERSION PAGES ({submittedConversionPages.length}/{visibleConversionPages.length})</Text>
                  {visibleConversionPages.length === 0 ? (
                    <Text fz={14} c="dimmed">This campaign has no other pages in its funnel.</Text>
                  ) : (
                    <Stack spacing={10} style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {visibleConversionPages.map((page:any) => {
                        const checked = checkedPages.includes(page.page_id);
                        return (
                          <Box
                            key={page.page_id}
                            component="label"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 14,
                              padding: '12px 16px',
                              borderRadius: 12,
                              cursor: duplicating ? 'default' : 'pointer',
                              background: cardBg,
                              border: `1px solid ${checked ? (dark ? primary[7] : primary[3]) : cardBorder}`,
                              opacity: checked ? 1 : 0.65,
                            }}
                          >
                            <Checkbox checked={checked} onChange={() => togglePage(page.page_id)} disabled={duplicating} size="md" radius="sm"/>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fz={15} fw={600} truncate>{page.title}</Text>
                              {page.url && (<Text fz={12.5} c="dimmed" style={{ fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}>{page.url}</Text>)}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                  <Text fz={12} c="dimmed" mt={8} lh={1.5}>Goals on unticked pages, button goals and postback goals (tied to the old landing page) are not copied. All other goals, webhooks and conversion API settings come across.</Text>
                </Box>
              </>
            )}
        </Stack>
        <Group position="right" mt={24}>
          <Button variant="default" radius="md" onClick={handleDuplicateCampaignCloseModal} disabled={duplicating}>Cancel</Button>
          <Button radius="md" leftIcon={<IconCopy size={16} />} onClick={() => submitDuplicateCampaign(false)} loading={duplicating} disabled={pagesLoading || !sourceCampaign}>Create Campaign</Button>
        </Group>
      </Modal>
    </>
  );
}
